import { ViesStatus } from 'store/app/enums';
import { DataContact } from 'store/vat/models/vatReportData';

const MIN_AMOUNT = 0;
const MAX_AMOUNT = 99999999999.99;
const LINE_ENDING = '\r\n';

export interface MaltaRecapPipeExportResult {
  success: boolean;
  content?: string;
  invalidVatContacts: string[];
  zeroAmountContacts: string[];
}

const clampAmount = (value: number): number => {
  if (Number.isNaN(value) || value < MIN_AMOUNT) {
    return MIN_AMOUNT;
  }
  if (value > MAX_AMOUNT) {
    return MAX_AMOUNT;
  }
  return value;
};

const formatDecimal = (value: number): string => {
  return clampAmount(value).toFixed(2);
};

const formatCustomerVatNo = (taxNumber: string | undefined): string => {
  if (!taxNumber) return '';
  return taxNumber.replace(/[\s.\-]/g, '').substring(0, 14);
};

const sanitizeCustomerName = (name: string | undefined): string => {
  if (!name) return '';
  const withoutTags = name.replace(/<[^>]*>/g, '');
  const textarea =
    typeof document !== 'undefined' ? document.createElement('textarea') : null;
  if (textarea) {
    textarea.innerHTML = withoutTags;
    return (textarea.value || withoutTags).trim();
  }
  return withoutTags
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();
};

const resolveTriangularTrade = (contact: DataContact): 0 | 1 => {
  const hasGoods = clampAmount(contact.goodsSum) > 0;
  const hasServices = clampAmount(contact.servicesSum) > 0;
  if (!hasGoods && hasServices) {
    return 0;
  }
  return contact.isTriangularTrade ? 1 : 0;
};

const isValidVatContact = (contact: DataContact): boolean => {
  if (!contact.taxNumber) return false;
  if (
    contact.viesStatus &&
    ViesStatus[contact.viesStatus] !== ViesStatus.VALID
  ) {
    return false;
  }
  return true;
};

const buildRow = (contact: DataContact): string => {
  const triangularTrade = resolveTriangularTrade(contact);
  const customerVatNo = formatCustomerVatNo(contact.taxNumber);
  const customerName = sanitizeCustomerName(contact.name);
  const totalGoods = formatDecimal(contact.goodsSum);
  const totalServices = formatDecimal(contact.servicesSum);

  return [
    String(triangularTrade),
    customerVatNo,
    customerName,
    totalGoods,
    totalServices,
  ].join('|');
};

export const buildMaltaRecapPipeFile = (
  contacts: DataContact[]
): MaltaRecapPipeExportResult => {
  const invalidVatContacts: string[] = [];
  const zeroAmountContacts: string[] = [];

  contacts.forEach((contact) => {
    if (!isValidVatContact(contact)) {
      if (!invalidVatContacts.includes(contact.name)) {
        invalidVatContacts.push(contact.name);
      }
      return;
    }

    const totalGoods = formatDecimal(contact.goodsSum);
    const totalServices = formatDecimal(contact.servicesSum);
    if (totalGoods === '0.00' && totalServices === '0.00') {
      if (!zeroAmountContacts.includes(contact.name)) {
        zeroAmountContacts.push(contact.name);
      }
    }
  });

  if (invalidVatContacts.length > 0 || zeroAmountContacts.length > 0) {
    return {
      success: false,
      invalidVatContacts,
      zeroAmountContacts,
    };
  }

  const rows = contacts.map((contact) => buildRow(contact));
  return {
    success: true,
    content: rows.join(LINE_ENDING) + LINE_ENDING,
    invalidVatContacts: [],
    zeroAmountContacts: [],
  };
};

export const downloadTextFile = (
  content: string,
  filename: string,
  mimeType = 'text/plain'
): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  link.click();
  window.URL.revokeObjectURL(url);
};
